import { FeatureFlag } from '@ps/web-core-enums';

import type { WidgetEffectParams } from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorCode from '@swf/sports/utils/getErrorCode';

import type { EventInfoSuccessResponse } from '../types';

import type { WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, widget, select, rootState, actions } = params;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];
    const {
      eventInfoRequested,
      dataVisRequested,
      clear,
      preloaded,
      quickSwitcherRequested
    }: WidgetDispatch = widgetDispatch;

    sportsWidgetPollingBehavior(params).subscribe({
      next: () => {
        eventInfoRequested();
      },
      complete: () => clear()
    });

    pageChange(actions)?.subscribe({
      next: () => {
        quickSwitcherRequested();
        dataVisRequested();
      },
      complete: () => clear()
    });

    if (!hasPreloaded(rootState)) {
      await Promise.allSettled([dataVisRequested(), eventInfoRequested()]);
      preloaded();
    }

    quickSwitcherRequested();
  },

  async eventInfoRequested(
    _payload: undefined,
    { dispatch, request, widget, select, rootState }: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const { eventId } = select.sports.getUrlParams(rootState);
    const sportsRequest = buildSportsRequest(request, rootState);

    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];

    try {
      const {
        body: { data }
      } = await sportsRequest<EventInfoSuccessResponse>({
        path: `/event-info/${eventId}`
      });
      widgetDispatch.dataLoadSuccess(data);
    } catch (error) {
      widgetDispatch.dataLoadFailed(getErrorCode(error));
    }
  },

  async dataVisRequested(
    _payload: undefined,
    { dispatch, request, widget, select, rootState }: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const { eventId } = select.sports.getUrlParams(rootState);
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);
    const isDataVisEnabled = select.feature.check(rootState, FeatureFlag.ISPDataVisualisation);

    if (!isDataVisEnabled) return;

    try {
      await sportsRequest({
        path: `/gbp/bvp/`,
        method: 'HEAD',
        body: {
          contentType: 'vis',
          eID: eventId,
          tr: 10,
          allowPopup: true
        }
      });

      widgetDispatch.dataVisLoadResult(true);
    } catch (error) {
      widgetDispatch.dataVisLoadResult(false);
    }
  },

  async quickSwitcherRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, rootState, select } = params;

    const { eventId } = select.sports.getUrlParams(rootState);

    dispatch.quickSwitcher.quickSwitcherRequested({
      hierarchyLevel: 'event',
      facetId: eventId
    });
  }
};
